import * as React from "react";
import apiClient from "@/api-client";
import { Box, LinearProgress, Stack, Typography } from "@mui/material";

interface CallLogAudioPlayerProps {
	/**
	 * API path the recording is fetched from, or null when there is nothing to play.
	 *
	 * Taken as a parameter rather than derived from a call, because the same player serves
	 * real calls and Proving Ground cells and those are not the same resource — a simulated
	 * call has no row in `calls`, so `/calls/:id/recording` answers 404 for one of them.
	 */
	recordingUrl: string | null;
	show: boolean;
	onTimeUpdate?: (currentTime: number) => void;
}

export function CallLogAudioPlayer({ recordingUrl, show, onTimeUpdate }: CallLogAudioPlayerProps): React.JSX.Element {
	const [isAudioLoading, setIsAudioLoading] = React.useState(false);
	const [audioError, setAudioError] = React.useState<string | null>(null);
	const [audioSrc, setAudioSrc] = React.useState<string | null>(null);

	const audioRef = React.useRef<HTMLAudioElement | null>(null);
	const audioAbortControllerRef = React.useRef<AbortController | null>(null);
	const previousUrlRef = React.useRef<string | null>(null);
	const blobUrlRef = React.useRef<string | null>(null);

	const loadRecording = React.useCallback(async () => {
		if (!recordingUrl) return;

		if (audioAbortControllerRef.current) {
			audioAbortControllerRef.current.abort();
		}

		const abortController = new AbortController();
		audioAbortControllerRef.current = abortController;

		setIsAudioLoading(true);
		setAudioError(null);

		try {
			const response = await apiClient.get(recordingUrl, {
				responseType: "blob",
				signal: abortController.signal,
			});

			if (abortController.signal.aborted) {
				return;
			}

			if (response.data && audioRef.current) {
				if (blobUrlRef.current) {
					URL.revokeObjectURL(blobUrlRef.current);
					blobUrlRef.current = null;
				}

				const blob = new Blob([response.data], { type: "audio/mpeg" });
				const blobUrl = URL.createObjectURL(blob);
				blobUrlRef.current = blobUrl;

				audioRef.current.src = blobUrl;
				audioRef.current.preload = "metadata";
				audioRef.current.load();
				setAudioSrc(blobUrl);

				// Autoplay is a courtesy, not the point. A browser that blocks it throws
				// NotAllowedError, which is not an AbortError and so used to fall through to the
				// catch below and paint "Failed to load recording." over a perfectly loaded file.
				try {
					await audioRef.current.play();
				} catch {
					// The operator can press play themselves.
				}
			}
		} catch (error_: any) {
			if (error_.name === "AbortError" || error_.message === "canceled") {
				return;
			}
			console.error("Failed to load recording:", error_);
			// A recording still being merged answers with a reason worth reading ("still being
			// processed, try again shortly") rather than a failure. The request asked for a blob,
			// so an error body arrives as one too and has to be read back out before it is JSON.
			let message = "Failed to load recording.";
			const body = error_?.response?.data;
			if (body instanceof Blob) {
				try {
					message = (JSON.parse(await body.text()) as { message?: string })?.message ?? message;
				} catch {
					// Not JSON. Keep the generic message.
				}
			} else if (typeof body?.message === "string") {
				message = body.message;
			}
			setAudioError(message);
		} finally {
			setIsAudioLoading(false);
			if (audioAbortControllerRef.current === abortController) {
				audioAbortControllerRef.current = null;
			}
		}
	}, [recordingUrl]);

	React.useEffect(() => {
		if (show && recordingUrl && !audioSrc && !isAudioLoading && !audioAbortControllerRef.current) {
			loadRecording();
		}
	}, [show, recordingUrl, audioSrc, isAudioLoading, loadRecording]);

	React.useEffect(() => {
		const audioEl = audioRef.current;

		if (recordingUrl !== previousUrlRef.current && previousUrlRef.current !== null) {
			if (blobUrlRef.current) {
				URL.revokeObjectURL(blobUrlRef.current);
				blobUrlRef.current = null;
			}
			if (audioEl) {
				audioEl.src = "";
				audioEl.pause();
			}
			setAudioSrc(null);
			setAudioError(null);
		}

		if (!recordingUrl) {
			if (blobUrlRef.current) {
				URL.revokeObjectURL(blobUrlRef.current);
				blobUrlRef.current = null;
			}
			if (audioEl) {
				audioEl.src = "";
				audioEl.pause();
			}
			setAudioSrc(null);
			setAudioError(null);
		}

		previousUrlRef.current = recordingUrl;
	}, [recordingUrl]);

	// Held in a ref so the listener below never has to be re-subscribed. Both callers pass an
	// inline arrow, so its identity changes on every parent render.
	const onTimeUpdateRef = React.useRef(onTimeUpdate);
	React.useEffect(() => {
		onTimeUpdateRef.current = onTimeUpdate;
	}, [onTimeUpdate]);

	React.useEffect(() => {
		const audioEl = audioRef.current;
		if (!audioEl) return;

		const handleTimeUpdate = () => onTimeUpdateRef.current?.(audioEl.currentTime);
		audioEl.addEventListener("timeupdate", handleTimeUpdate);
		return () => audioEl.removeEventListener("timeupdate", handleTimeUpdate);
	}, []);

	/**
	 * Aborts the download on unmount, and only on unmount.
	 *
	 * This used to sit in the timeupdate effect, keyed on the `onTimeUpdate` callback. Both
	 * callers pass an inline arrow, so every parent render tore that effect down and cancelled
	 * the in-flight fetch - then the loader effect saw `isAudioLoading` back at false and
	 * started again from byte zero. With a run page polling every three seconds, a recording
	 * that takes longer than that to arrive never finished loading at all.
	 */
	React.useEffect(() => {
		return () => {
			audioAbortControllerRef.current?.abort();
			audioAbortControllerRef.current = null;
		};
	}, []);

	React.useEffect(() => {
		return () => {
			if (blobUrlRef.current) {
				URL.revokeObjectURL(blobUrlRef.current);
				blobUrlRef.current = null;
			}
		};
	}, []);

	return (
		<Box
			sx={{
				py: show ? 1 : 0,
				px: 2,
				borderBottom: show ? 1 : 0,
				borderColor: "divider",
				opacity: show ? 1 : 0,
				height: show ? "auto" : 0,
				pointerEvents: show ? "auto" : "none",
			}}
		>
			<Stack spacing={2}>
				<Box sx={{ position: "relative" }}>
					<Box sx={{ opacity: isAudioLoading || audioError ? 0 : 1, height: "40px" }}>
						<audio
							ref={audioRef}
							controls
							preload="metadata"
							style={{ width: "100%", outline: "none", height: "100%" }}
						/>
					</Box>
					{isAudioLoading && (
						<Box
							sx={{
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
							}}
						>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
								Loading recording...
							</Typography>
							<LinearProgress />
						</Box>
					)}

					{audioError && (
						<Box
							sx={{
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
							}}
						>
							<Box
								sx={{
									p: 1,
									bgcolor: "error.light",
									color: "error.contrastText",
									borderRadius: 1,
								}}
							>
								<Typography variant="body2">{audioError}</Typography>
							</Box>
						</Box>
					)}
				</Box>
			</Stack>
		</Box>
	);
}
